import BirthdayPromotionContent from 'routes/Tickets/sections/BirthdayPromotion/BirthdayPromotionContent'
import { ReactElement, useMemo } from 'react'
import BlackWeekendTicketsInitialInfo from 'components/shared/Promotions/components/BlackWeekendTicketsInitialInfo'
import { usePromotions } from 'components/shared/Promotions/utils/usePromotions'
import BirthdayPromotionInfo from 'components/global/promotions/BirthdayPromotion/BirthdayPromotionInfo'
import BlackWeekendCabanaInfo from 'components/shared/Promotions/components/BlackWeekendCabanaInfo'
import BirthdayPromotionTickets from 'components/global/promotions/BirthdayPromotion/BirthdayPromotionTickets'
import BlackWeekendInfo from 'components/shared/Promotions/components/BlackWeekendInfo'
import BirthdayPromotionCode from 'components/global/promotions/BirthdayPromotion/BirthdayPromotionCode'
import { HeaderBirthdayCap } from 'components/global/Header/styles'
import birthdayCap from 'assets/images/logos/birthday-cap.svg'
import BlackWeekendUnlimitedInitialInfo from 'components/shared/Promotions/components/BlackWeekendUnlimitedInitialInfo'
import BirthdayUnlimitedPromotionContent from 'routes/Tickets/sections/BirthdayPromotion/BirthdayUnlimitedPromotionContent'
import { BlackWeekendCalendar } from 'components/shared/Calendar/components/BlackWeekendCalendar'
import { CustomMonthPromoProps } from 'components/shared/Calendar/components/CustomMonth'
import { BirthdayPromoCalendar } from 'components/shared/Calendar/components/BirthdayPromoCalendar'
import BlackWeekendUnlimitedInfo from 'components/shared/Promotions/components/BlackWeekendUnlimitedInfo'
import BirthdayUnlimitedPromotionInfo from 'routes/Tickets/sections/BirthdayPromotion/BirthdayUnlimitedPromotionInfo'
import { useApp } from 'store/steps'
import SummerSalePromotionCode from 'components/global/promotions/SummerSale/SummerSalePromotionCode'
import SummerSaleTicketsInitialInfo from 'components/global/promotions/SummerSale/SummerSaleTicketsInitialInfo'
import SummerSaleTicketsConfigInfo from 'components/global/promotions/SummerSale/SummerSaleTicketsConfigInfo'
import SummerSaleCabanaInfo from 'components/global/promotions/SummerSale/SummerSaleCabanaInfo'
import { SummerSaleCalendar } from 'components/shared/Calendar/components/SummerSaleCalendar'
import AugustPromotionInitialInfo from 'routes/Tickets/sections/AugustPromotion/AugustPromotionInitialInfo'

interface TicketPromotionsModel {
    name: string
    tickets: PromotionType
    unlimited: PromotionType
}

interface PromotionType {
    isActive: boolean
    initialInfo: ReactElement | null
    cabana: ReactElement | null
    creator: ReactElement | null
    discountCode: ReactElement | null
    logoImage: ReactElement | null
    calendar:
        | ((props: Omit<CustomMonthPromoProps, 'year'>) => ReactElement)
        | null
}

/* Black Friday, Birthday Promo, or any other dynamic promotion should be created using the usePromotions hook and connected with useTicketPromotions.
After that, you renderActivePromotions by passing a key to allow turning on the promotion (which is inside the key-value pair isActive). */

export const useTicketPromotions = (isPromoActive?: boolean) => {
    const { isCreatorAccommodation } = useApp()
    const {
        isBlackPromoForTickets,
        isBlackPromoForCabanas,
        isBirthdayPromoForTickets,
        isBirthdayPromoForCabanas,
        isBlackPromoForCreator,
        isBlackPromoForUnlimited,
        isBirthdayPromoForCreator,
        isBirthdayPromoForUnlimited,
        isSummerSaleForTickets,
        isSummerSaleForCabanas,
        isSummerSaleForCreator,
        getPromotionByName,
    } = usePromotions()

    const ticketPromotions: TicketPromotionsModel[] = useMemo(
        () => [
            {
                name: 'August Promotion',
                tickets: {
                    isActive: getPromotionByName(
                        'August Promotion',
                        'isActiveForTickets'
                    ),
                    initialInfo: isCreatorAccommodation ? null : (
                        <AugustPromotionInitialInfo />
                    ),
                    cabana: null,
                    creator: null,
                    discountCode: null,
                    logoImage: null,
                    calendar: null,
                },
                unlimited: {
                    isActive: false,
                    initialInfo: null,
                    cabana: null,
                    creator: null,
                    discountCode: null,
                    logoImage: null,
                    calendar: null,
                },
            },
            {
                name: 'Summer Sale',
                tickets: {
                    isActive: !!isSummerSaleForCreator,
                    initialInfo: isSummerSaleForTickets ? (
                        <SummerSaleTicketsInitialInfo mb={24} mt={24} />
                    ) : null,
                    creator: isSummerSaleForTickets ? (
                        <SummerSaleTicketsConfigInfo mt={24} />
                    ) : null,
                    cabana: isSummerSaleForCabanas ? (
                        <SummerSaleCabanaInfo />
                    ) : null,
                    discountCode: <SummerSalePromotionCode />,
                    logoImage: null,
                    calendar: isSummerSaleForTickets
                        ? (props: Omit<CustomMonthPromoProps, 'year'>) => (
                              <SummerSaleCalendar {...props} />
                          )
                        : null,
                },
                unlimited: {
                    isActive: false,
                    initialInfo: null,
                    cabana: null,
                    creator: null,
                    discountCode: null,
                    logoImage: null,
                    calendar: null,
                },
            },
            {
                name: 'Black Weekend',
                tickets: {
                    isActive: !!isBlackPromoForCreator,
                    initialInfo: isBlackPromoForTickets ? (
                        <BlackWeekendTicketsInitialInfo pt={[0, 12]} mb={24} />
                    ) : null,
                    cabana: isBlackPromoForCabanas ? (
                        <BlackWeekendCabanaInfo />
                    ) : null,
                    creator: isBlackPromoForTickets ? (
                        <BlackWeekendInfo />
                    ) : null,
                    discountCode: null,
                    logoImage: null,
                    calendar: isBlackPromoForTickets
                        ? (props: Omit<CustomMonthPromoProps, 'year'>) => (
                              <BlackWeekendCalendar {...props} />
                          )
                        : null,
                },
                unlimited: {
                    isActive: !!isBlackPromoForUnlimited,
                    initialInfo: <BlackWeekendUnlimitedInitialInfo />,
                    cabana: <BlackWeekendCabanaInfo />,
                    creator: <BlackWeekendUnlimitedInfo />,
                    discountCode: null,
                    logoImage: null,
                    calendar: null,
                },
            },
            {
                name: 'Birthday',
                tickets: {
                    isActive: !!isBirthdayPromoForCreator,
                    initialInfo: isBirthdayPromoForTickets ? (
                        <BirthdayPromotionContent pt={[0, 12]} mb={24} />
                    ) : null,
                    cabana: isBirthdayPromoForCabanas ? (
                        <BirthdayPromotionInfo />
                    ) : null,
                    creator: isBirthdayPromoForTickets ? (
                        <BirthdayPromotionTickets />
                    ) : null,
                    discountCode: <BirthdayPromotionCode />,
                    logoImage: (
                        <HeaderBirthdayCap
                            src={birthdayCap}
                            alt="Park of Poland logo"
                        />
                    ),
                    calendar: isBirthdayPromoForTickets
                        ? (props: Omit<CustomMonthPromoProps, 'year'>) => (
                              <BirthdayPromoCalendar {...props} />
                          )
                        : null,
                },
                unlimited: {
                    isActive: !!isBirthdayPromoForUnlimited,
                    initialInfo: <BirthdayUnlimitedPromotionContent />,
                    cabana: null,
                    creator: <BirthdayUnlimitedPromotionInfo />,
                    discountCode: <BirthdayPromotionCode />,
                    logoImage: (
                        <HeaderBirthdayCap
                            src={birthdayCap}
                            alt="Park of Poland logo"
                        />
                    ),
                    calendar: null,
                },
            },
        ],
        [
            isPromoActive,
            isSummerSaleForCreator,
            isBlackPromoForCreator,
            isBirthdayPromoForCreator,
        ]
    )

    const renderActivePromotions = <
        T extends Omit<CustomMonthPromoProps, 'year'>
    >({
        key,
        sectionType = 'tickets',
        component,
        props,
    }: {
        key?: keyof PromotionType
        sectionType?: keyof Pick<TicketPromotionsModel, 'tickets' | 'unlimited'>
        component?: ReactElement
        props?: T
    } = {}) => {
        const activePromotions = ticketPromotions.filter(
            (promotion) => promotion[sectionType].isActive
        )

        if (activePromotions.length === 0) {
            return component ?? null
        }

        if (key) {
            const selectedPromotion = activePromotions.find(
                (promotion) => promotion[sectionType][key]
            )

            if (selectedPromotion) {
                const promotionWithFunction =
                    selectedPromotion[sectionType][key]
                if (
                    promotionWithFunction &&
                    typeof promotionWithFunction === 'function' &&
                    props
                ) {
                    return selectedPromotion[sectionType].isActive
                        ? promotionWithFunction(props)
                        : null
                }
                return selectedPromotion[sectionType].isActive
                    ? selectedPromotion[sectionType][key]
                    : component || null
            }
            return component || null
        }
    }

    return { ticketPromotions, renderActivePromotions }
}
